import { TENANT_SWITCH_TARGETS } from "@/configs/publicTenantConfig";

export const COUNTRY_NOTICE_TENANTS = ["AT", "DE"];

const localeCountryAliasMap = {
  GB: "UK",
};

export function isCountryNoticeTenant(tenantCode) {
  return COUNTRY_NOTICE_TENANTS.includes(tenantCode);
}

export function regionFromLocaleTag(tag) {
  if (!tag || typeof tag !== "string") {
    return null;
  }

  const parts = tag.split("-").map((part) => part.trim()).filter(Boolean);
  let lastRegion = null;

  for (let i = 1; i < parts.length; i += 1) {
    const part = parts[i];
    if (part.length === 2 && /^[A-Za-z]{2}$/.test(part)) {
      lastRegion = part.toUpperCase();
    }
  }

  if (!lastRegion) {
    return null;
  }

  return localeCountryAliasMap[lastRegion] || lastRegion;
}

export function collectLocaleTags() {
  const tags = [];
  const seen = new Set();

  const add = (tag) => {
    if (tag && typeof tag === "string" && !seen.has(tag)) {
      seen.add(tag);
      tags.push(tag);
    }
  };

  if (navigator.languages?.length) {
    navigator.languages.forEach(add);
  }

  add(navigator.language);

  try {
    add(Intl.DateTimeFormat().resolvedOptions().locale);
  } catch {
    /* ignore */
  }

  try {
    add(Intl.NumberFormat().resolvedOptions().locale);
  } catch {
    /* ignore */
  }

  return tags;
}

export function getBrowserSuggestedTenantFromLocaleOnly(
  currentTenantCode,
  localeTags = collectLocaleTags()
) {
  for (const tag of localeTags) {
    const normalized = regionFromLocaleTag(tag);
    if (normalized && isCountryNoticeTenant(normalized)) {
      return normalized;
    }
  }

  if (currentTenantCode === "AT") {
    for (const tag of localeTags) {
      const primary = (tag || "").split("-")[0].toLowerCase();
      if (primary === "de") {
        return "DE";
      }
    }
  }

  return null;
}

export function getBrowserSuggestedTenant(
  currentTenantCode,
  localeTags = collectLocaleTags()
) {
  const fromLocale = getBrowserSuggestedTenantFromLocaleOnly(
    currentTenantCode,
    localeTags
  );

  if (fromLocale && fromLocale !== currentTenantCode) {
    return fromLocale;
  }

  return null;
}

export function normalizeForcedVisitTenantCode(rawValue) {
  const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  const code = value?.toString().toUpperCase().trim();

  if (code && isCountryNoticeTenant(code)) {
    return code;
  }

  return null;
}

export function getPublicCountryNoticeContext(currentTenantCode, forcedVisitTenantRaw) {
  const localeTags = collectLocaleTags();
  const forcedVisitTenantCode = normalizeForcedVisitTenantCode(forcedVisitTenantRaw);
  const browserSuggestedTenant = getBrowserSuggestedTenant(currentTenantCode, localeTags);
  const suggestedTenant = forcedVisitTenantCode || browserSuggestedTenant;
  const hasMismatch = Boolean(
    currentTenantCode &&
    suggestedTenant &&
    suggestedTenant !== currentTenantCode &&
    TENANT_SWITCH_TARGETS[currentTenantCode] === suggestedTenant
  );

  return {
    localeTags,
    forcedVisitTenantCode,
    browserSuggestedTenant,
    suggestedTenant: hasMismatch ? suggestedTenant : null,
    inferredCountry: hasMismatch ? suggestedTenant : null,
    detection: forcedVisitTenantCode
      ? "force_locale_hint"
      : browserSuggestedTenant && browserSuggestedTenant !== currentTenantCode
        ? "browser_locale_intl"
        : null,
    hasMismatch,
  };
}
